<template>
  <div class="third-party-model-page" :class="{ 'is-mobile': isMobile }">
    <!-- ================= 两栏主体：左侧余额总览 / 右侧表格 ================= -->
    <!-- 移动端只展示右侧列表，余额总览整块隐藏 -->
    <div class="main-layout">
      <!--
        左侧余额总览：每个已接入余额查询方法的大模型占一个方块，从上往下排列。
        方块内部的布局按来源（source）分支渲染，新增厂商时在这里加一个分支即可；
        后端只负责把结构化数据取回来。
      -->
      <aside v-if="!isMobile" class="layout-aside">
          <section v-if="balanceCards.length" class="balance-board">
        <div class="balance-board__head">
          <div class="balance-board__title">
            <span>余额总览</span>
            <span class="balance-board__count">{{ balanceCards.length }} 个来源</span>
          </div>
          <el-button
            type="text"
            size="mini"
            icon="el-icon-refresh"
            :loading="balanceRefreshing"
            :disabled="balanceRefreshing"
            @click="handleRefreshBalance"
          >刷新余额</el-button>
        </div>

        <div v-loading="balanceLoading" class="balance-board__grid">
          <div
            v-for="card in balanceCards"
            :key="card.source"
            class="balance-card"
            :class="{ 'is-error': !card.ok }"
          >
            <div class="balance-card__head">
              <span class="balance-card__source">{{ card.source }}</span>
            </div>

            <!-- 商汤大模型：套餐 + 各积分池明细 -->
            <div v-if="card.source === '商汤大模型'" class="balance-card__body">
              <template v-if="card.ok && card.data">
                <div class="balance-card__pools">
                    <div
                      v-for="pool in card.data.pools"
                    :key="pool.id || pool.name"
                    class="bl-pool"
                  >
                    <div class="bl-pool__head">
                      <span class="bl-pool__name">{{ pool.name }}</span>
                      <span class="bl-pool__scope">{{ pool.scopeText }}</span>
                    </div>

                    <div class="bl-grid bl-grid--vertical">
                      <div class="bl-cell">
                        <div class="bl-cell__label">5h 窗口可用</div>
                        <div class="bl-cell__value">{{ formatNumber(pool.window5h.remaining) }}</div>
                        <div class="bl-bar">
                          <i :style="{ width: pool.window5h.percent + '%' }"></i>
                        </div>
                        <div class="bl-cell__sub">
                          {{ formatNumber(pool.window5h.used) }} / {{ formatNumber(pool.window5h.limit) }}
                          · {{ pool.window5h.percent }}%
                        </div>
                      </div>

                      <div class="bl-cell">
                        <div class="bl-cell__label">周额度</div>
                        <div class="bl-cell__value">{{ formatNumber(pool.window7d.limit) }}</div>
                        <div class="bl-cell__sub">已用 {{ formatNumber(pool.window7d.used) }}</div>
                        <div class="bl-cell__sub">下次重置 {{ pool.window7d.resetText || '—' }}</div>
                      </div>

                      <div class="bl-cell is-highlight">
                        <div class="bl-cell__label">活动固定积分 · 总量余额</div>
                        <div class="bl-cell__value is-primary">{{ pool.grantBalanceText }}</div>
                        <div class="bl-cell__sub">最近一次到期 {{ pool.nearestGrantExpiryText || '—' }}</div>
                        <div class="bl-cell__sub">到期金额 {{ pool.nearestGrantExpiringBalanceText }}</div>
                      </div>
                    </div>

                      <div class="bl-pool__reset">5h 窗口重置 {{ pool.window5h.resetText || '—' }}</div>
                    </div>
                  </div>
                </template>

                <div v-else class="balance-card__empty">
                  {{ card.message || '尚未查询' }}
                </div>
              </div>

            <!-- 清枢智汇：余额 + 总消费（官方口径） -->
            <div v-else-if="card.source === '清枢智汇'" class="balance-card__body">
              <template v-if="card.ok && card.data">
                <div class="bl-grid bl-grid--vertical">
                  <div class="bl-cell is-highlight">
                    <div class="bl-cell__label">账户余额</div>
                    <div class="bl-cell__value is-primary">${{ card.data.balanceText }}</div>
                    <div class="bl-cell__sub">共 {{ formatNumber(card.data.balanceQuota) }} 额度</div>
                  </div>

                  <div class="bl-cell">
                    <div class="bl-cell__label">总消费</div>
                    <div class="bl-cell__value">${{ card.data.consumedText }}</div>
                    <div class="bl-cell__sub">共 {{ card.data.logCount }} 条调用记录</div>
                  </div>

                  <div class="bl-cell">
                    <div class="bl-cell__label">赠送额度</div>
                    <div class="bl-cell__value">${{ card.data.giftQuotaText }}</div>
                    <div class="bl-cell__sub">账户 {{ card.data.username || '—' }}</div>
                  </div>
                </div>
              </template>

              <div v-else class="balance-card__empty">
                {{ card.message || '尚未查询' }}
              </div>
            </div>

            <!-- 云栖小铺：余额 + 充值/消费 -->
            <div v-else-if="card.source === '云栖小铺'" class="balance-card__body">
              <template v-if="card.ok && card.data">
                <div class="bl-grid bl-grid--vertical">
                  <div class="bl-cell is-highlight">
                    <div class="bl-cell__label">账户余额</div>
                    <div class="bl-cell__value is-primary">${{ card.data.balanceText }}</div>
                    <div class="bl-cell__sub">冻结 ${{ card.data.frozenBalanceText }}</div>
                  </div>

                  <div class="bl-cell">
                    <div class="bl-cell__label">累计充值</div>
                    <div class="bl-cell__value">${{ card.data.totalRechargedText }}</div>
                    <div class="bl-cell__sub">已消费 ${{ card.data.consumedText }}</div>
                  </div>

                  <div class="bl-cell">
                    <div class="bl-cell__label">账户状态</div>
                    <div class="bl-cell__value">{{ card.data.status === 'active' ? '正常' : card.data.status }}</div>
                    <div class="bl-cell__sub">{{ card.data.email || '—' }}</div>
                  </div>
                </div>
              </template>

              <div v-else class="balance-card__empty">
                {{ card.message || '尚未查询' }}
              </div>
            </div>

            <!-- 其他来源：还没有专属布局时先给出摘要占位 -->
            <div v-else class="balance-card__body">
              <div v-if="card.ok" class="bl-fallback">{{ card.summary || card.message || '查询成功' }}</div>
              <div v-else class="balance-card__empty">{{ card.message || '尚未查询' }}</div>
            </div>
          </div>
            </div>
          </section>
      </aside>

      <!-- 右侧表格 -->
      <main class="layout-main">
          <div class="page-card">
        <!-- 工具条：表头左侧的新建 / 批量测试 -->
        <div class="table-toolbar">
          <div v-if="!isMobile" class="table-toolbar__left">
            <el-button
              type="primary"
              size="small"
              icon="el-icon-plus"
              @click="openCreateDialog"
            >新建</el-button>
            <el-button
              type="success"
              size="small"
              plain
              icon="el-icon-refresh"
              :disabled="!list.length || batchTesting"
              :loading="batchTesting"
              @click="handleBatchTest"
            >批量测试</el-button>
          </div>
          <div class="table-toolbar__right">
            <el-button
              v-if="!isMobile"
              type="text"
              icon="el-icon-refresh"
              :disabled="loading"
              @click="loadList"
            >刷新列表</el-button>
            <span class="meta-chip">共 {{ list.length }} 个来源</span>
            <span class="meta-chip is-success">可用 {{ availableCount }}</span>
            <span class="meta-chip is-danger">不可用 {{ unavailableCount }}</span>
          </div>
        </div>

        <!-- 数据表格（桌面端：完整字段） -->
        <el-table
          v-if="!isMobile"
          v-loading="loading"
          :data="pagedList"
          border
          size="small"
          class="third-party-table"
          row-key="id"
        >
          <el-table-column prop="source" label="来源" min-width="100" show-overflow-tooltip>
            <template slot-scope="{ row }">
              <span class="cell-source">{{ row.source || '—' }}</span>
            </template>
          </el-table-column>

          <el-table-column prop="owner" label="归属" width="90" align="center">
            <template slot-scope="{ row }">
              <span class="cell-owner" :class="ownerTagClass(row.owner)">{{ row.owner || '—' }}</span>
            </template>
          </el-table-column>

          <el-table-column label="URL" min-width="200" show-overflow-tooltip>
            <template slot-scope="{ row }">
              <a
                v-if="row.url"
                class="cell-url"
                :href="row.url"
                target="_blank"
                rel="noopener noreferrer"
              >{{ row.url }}</a>
              <span v-else>—</span>
            </template>
          </el-table-column>

          <el-table-column label="Key" min-width="180">
            <template slot-scope="{ row }">
              <div class="cell-key">
                <span class="cell-key__text">{{ visibleKeyIds.indexOf(row.id) === -1 ? maskKey(row.key) : row.key }}</span>
                <el-button
                  type="text"
                  size="mini"
                  class="cell-key__action"
                  icon="el-icon-view"
                  :title="visibleKeyIds.indexOf(row.id) === -1 ? '显示密钥' : '隐藏密钥'"
                  @click="toggleKeyVisible(row)"
                />
                <el-button
                  type="text"
                  size="mini"
                  class="cell-key__action"
                  icon="el-icon-document-copy"
                  title="复制密钥"
                  @click="copyKey(row)"
                />
              </div>
            </template>
          </el-table-column>

          <el-table-column label="上传时间" width="150" align="center">
            <template slot-scope="{ row }">
              <span class="cell-time">{{ formatDate(row.createdAt) }}</span>
            </template>
          </el-table-column>

          <el-table-column label="更新时间" width="150" align="center">
            <template slot-scope="{ row }">
              <span class="cell-time">{{ formatDate(row.updatedAt) }}</span>
            </template>
          </el-table-column>

          <el-table-column label="是否可用" width="110" align="center">
            <template slot-scope="{ row }">
              <el-tooltip
                :disabled="!row.testMessage"
                :content="row.testMessage"
                placement="top"
              >
                <span class="status-tag" :class="statusClass(row)">
                  <i class="status-tag__dot"></i>
                  {{ statusText(row) }}
                </span>
              </el-tooltip>
            </template>
          </el-table-column>

          <el-table-column label="包含模型" width="110" align="center">
            <template slot-scope="{ row }">
              <el-button
                type="text"
                size="mini"
                class="cell-model-count"
                :disabled="!row.modelCount"
                @click="openModelsDialog(row)"
              >{{ row.modelCount }} 个</el-button>
            </template>
          </el-table-column>

          <el-table-column label="操作" width="170" align="center" fixed="right">
            <template slot-scope="{ row }">
              <el-button
                type="text"
                size="mini"
                :loading="testingIds.indexOf(row.id) !== -1"
                @click="handleTest(row)"
              >测试</el-button>

              <el-button
                type="text"
                size="mini"
                @click="openDetailDialog(row)"
              >查看</el-button>

              <!-- 其余操作收进「更多」，鼠标悬浮展开 -->
              <el-dropdown
                trigger="hover"
                placement="bottom-end"
                class="row-more-dropdown"
                @command="command => handleMoreCommand(command, row)"
              >
                <span class="row-more">
                  更多<i class="el-icon-arrow-down el-icon--right"></i>
                </span>
                <el-dropdown-menu slot="dropdown">
                  <el-dropdown-item command="edit" icon="el-icon-edit">编辑</el-dropdown-item>
                  <el-dropdown-item command="models" icon="el-icon-tickets">查看模型</el-dropdown-item>
                  <el-dropdown-item command="delete" icon="el-icon-delete" divided>删除</el-dropdown-item>
                </el-dropdown-menu>
              </el-dropdown>
            </template>
          </el-table-column>

          <!-- 空状态 -->
          <template slot="empty">
            <div class="table-empty">
              <p>还没有第三方模型来源</p>
              <el-button type="primary" size="small" icon="el-icon-plus" @click="openCreateDialog">
                立即新建
              </el-button>
            </div>
          </template>
        </el-table>

        <!-- 移动端列表：字段只保留 来源 / 是否可用，操作只保留 测试 / 查看；
             固定高度 + 内部滚动，滚到底部自动追加下一页 -->
        <div
          v-else
          ref="mobList"
          v-loading="loading"
          class="mob-list"
          @scroll.passive="handleMobScroll"
        >
          <div
            v-for="row in mobList"
            :key="row.id"
            class="mob-row"
          >
            <span class="mob-row__source">{{ row.source || '—' }}</span>
            <span class="mob-row__status">
              <span class="status-tag" :class="statusClass(row)">
                <i class="status-tag__dot"></i>
                {{ statusText(row) }}
              </span>
            </span>
            <span class="mob-row__actions">
              <el-button
                type="text"
                size="mini"
                class="mob-row__action"
                :loading="testingIds.indexOf(row.id) !== -1"
                @click="handleTest(row)"
              >测试</el-button>
              <el-button
                type="text"
                size="mini"
                class="mob-row__action"
                @click="openDetailDialog(row)"
              >查看</el-button>
            </span>
          </div>

          <div v-if="!loading && !total" class="table-empty">
            <p>还没有第三方模型来源</p>
          </div>

          <!-- 触底加载状态 -->
          <div v-if="total" class="mob-list__footer">
            <span v-if="mobLoading">加载中…</span>
            <span v-else-if="mobHasMore">上拉加载更多</span>
            <span v-else>没有更多了</span>
          </div>
        </div>

        <!-- 分页器仅桌面端展示，移动端改为触底加载 -->
        <div v-if="!isMobile" class="table-pagination">
          <el-pagination
            background
            small
            layout="total, sizes, prev, pager, next, jumper"
            :current-page="page"
            :page-size="pageSize"
            :page-sizes="[10, 20, 50, 100]"
            :total="total"
            @size-change="handlePageSizeChange"
            @current-change="handlePageChange"
          />
        </div>
      </div>

      <!-- ================= 新建 / 编辑弹窗 ================= -->
      <el-dialog
        :title="formMode === 'create' ? '新建第三方模型' : '编辑第三方模型'"
        :visible.sync="formDialogVisible"
        width="min(520px, 92vw)"
        :close-on-click-modal="false"
        append-to-body
        class="third-party-form-dialog"
      >
        <el-form
          ref="formRef"
          :model="form"
          :rules="formRules"
          label-position="top"
          size="small"
          @submit.native.prevent
        >
          <el-form-item label="来源" prop="source">
            <el-input
              v-model.trim="form.source"
              maxlength="128"
              placeholder="例如：云栖小铺 / 某某中转站"
              @keyup.enter.native="submitForm"
            />
          </el-form-item>

          <el-form-item label="归属" prop="owner">
            <div class="owner-field">
              <el-select
                v-if="!ownerCustomMode"
                v-model="form.owner"
                placeholder="请选择归属"
                class="owner-field__select"
              >
                <el-option
                  v-for="item in ownerOptions"
                  :key="item"
                  :label="item"
                  :value="item"
                />
              </el-select>
              <el-input
                v-else
                v-model.trim="form.owner"
                maxlength="64"
                placeholder="请输入归属"
                class="owner-field__input"
                @keyup.enter.native="submitForm"
              />
              <el-button
                v-if="!ownerCustomMode"
                class="owner-field__action"
                icon="el-icon-plus"
                title="自定义归属"
                @click="enterOwnerCustomMode"
              />
              <el-button
                v-else
                class="owner-field__action"
                icon="el-icon-back"
                title="改回下拉选择"
                @click="exitOwnerCustomMode"
              />
            </div>
          </el-form-item>

          <el-form-item label="URL" prop="url">
            <el-input
              v-model.trim="form.url"
              maxlength="512"
              placeholder="例如：https://api.example.com/v1"
              @keyup.enter.native="submitForm"
            />
            <div class="form-tip">
              支持填写站点根地址或 <code>.../v1</code>，测试时会自动补全为 <code>/v1/models</code>。
            </div>
          </el-form-item>

          <el-form-item label="Key" prop="key">
            <el-input
              v-model.trim="form.key"
              type="password"
              show-password
              maxlength="512"
              placeholder="请输入接口密钥"
              @keyup.enter.native="submitForm"
            />
          </el-form-item>

          <!-- 已接入余额查询方法的来源，需要额外提供登录账号密码 -->
          <template v-if="formNeedsLogin">
            <el-divider content-position="left">余额查询登录凭证</el-divider>
            <el-form-item label="登录账号" prop="loginAccount">
              <el-input
                v-model.trim="form.loginAccount"
                maxlength="128"
                placeholder="该平台的登录账号"
                @keyup.enter.native="submitForm"
              />
            </el-form-item>
            <el-form-item label="登录密码" prop="loginPassword">
              <el-input
                v-model="form.loginPassword"
                type="password"
                show-password
                placeholder="该平台的登录密码"
                @keyup.enter.native="submitForm"
              />
              <div class="form-tip">
                <template v-if="formMode === 'edit' && hasStoredPassword">
                  留空表示不修改已保存的密码。
                </template>
                <template v-else>
                  密码加密后存储，页面上不会回显明文。
                </template>
              </div>
            </el-form-item>
          </template>
        </el-form>

        <div slot="footer">
          <el-button size="small" @click="formDialogVisible = false">取消</el-button>
          <el-button
            type="primary"
            size="small"
            :loading="submitting"
            @click="submitForm"
          >保存</el-button>
        </div>
      </el-dialog>

      <!-- ================= 查看详情弹窗：URL + Key + 全部模型 ================= -->
      <el-dialog
        :title="detailDialogTitle"
        :visible.sync="detailDialogVisible"
        width="min(560px, 92vw)"
        append-to-body
        class="third-party-detail-dialog"
      >
        <div v-if="detailRow" class="detail-body">
          <div class="detail-item">
            <div class="detail-item__label">
              <span>URL</span>
              <el-button
                type="text"
                size="mini"
                icon="el-icon-document-copy"
                class="detail-item__copy"
                @click="copyText(detailRow.url, 'URL 已复制')"
              >复制</el-button>
            </div>
            <a
              v-if="detailRow.url"
              class="detail-item__value detail-item__link"
              :href="detailRow.url"
              target="_blank"
              rel="noopener noreferrer"
            >{{ detailRow.url }}</a>
            <span v-else class="detail-item__value">—</span>
          </div>

          <div class="detail-item">
            <div class="detail-item__label">
              <span>Key</span>
              <el-button
                type="text"
                size="mini"
                icon="el-icon-document-copy"
                class="detail-item__copy"
                @click="copyText(detailRow.key, '密钥已复制')"
              >复制</el-button>
            </div>
            <div class="detail-item__value is-key" @click="detailKeyVisible = !detailKeyVisible">
              {{ detailKeyVisible ? (detailRow.key || '—') : maskKey(detailRow.key) }}
              <i :class="detailKeyVisible ? 'el-icon-view' : 'el-icon-view'"></i>
            </div>
            <div class="detail-item__tip">点击可切换明文 / 密文</div>
          </div>

          <div class="detail-item">
            <div class="detail-item__label">
              <span>全部模型</span>
              <span class="detail-item__count">{{ detailModels.length }} 个</span>
            </div>

            <div v-loading="detailModelsLoading" class="detail-models">
              <div
                v-for="name in detailModels"
                :key="name"
                class="detail-model"
                @click="copyText(name, '模型名已复制')"
              >{{ name }}</div>
              <div v-if="!detailModelsLoading && !detailModels.length" class="detail-models__empty">
                该来源暂无模型清单
              </div>
            </div>
          </div>
        </div>

        <div slot="footer">
          <el-button size="small" :disabled="!detailModels.length" @click="copyAllDetailModels">复制全部</el-button>
          <el-button size="small" type="primary" @click="detailDialogVisible = false">关闭</el-button>
        </div>
      </el-dialog>

      <!-- ================= 查看模型弹窗 ================= -->
      <el-dialog
        :title="modelsDialogTitle"
        :visible.sync="modelsDialogVisible"
        width="min(600px, 92vw)"
        append-to-body
        class="third-party-models-dialog"
      >
        <div v-loading="modelsLoading" class="models-body">
          <div v-if="modelsMessage" class="models-message">{{ modelsMessage }}</div>

          <template v-if="modelNames.length">
            <div class="models-toolbar">
              <el-input
                v-model.trim="modelKeyword"
                size="mini"
                prefix-icon="el-icon-search"
                placeholder="搜索模型"
                clearable
                class="models-search"
              />
              <el-button
                type="text"
                size="mini"
                icon="el-icon-refresh"
                :disabled="modelsLoading"
                @click="refreshModels"
              >重新拉取</el-button>
            </div>

            <ul class="models-list">
              <li
                v-for="name in filteredModelNames"
                :key="name"
                class="models-item"
              >
                <span class="models-item__name">{{ name }}</span>
                <el-button
                  type="text"
                  size="mini"
                  icon="el-icon-document-copy"
                  class="models-item__copy"
                  @click="copyText(name, '模型名已复制')"
                />
              </li>
            </ul>

            <div v-if="!filteredModelNames.length" class="models-empty">
              没有匹配「{{ modelKeyword }}」的模型
            </div>
          </template>

          <div v-else-if="!modelsLoading" class="models-empty">
            <p>该来源暂无可展示的模型</p>
            <el-button size="mini" type="primary" plain @click="refreshModels">立即拉取</el-button>
          </div>
        </div>

        <div slot="footer">
          <el-button
            size="small"
            :disabled="!modelNames.length"
            @click="copyAllModels"
          >复制全部</el-button>
          <el-button size="small" type="primary" @click="modelsDialogVisible = false">关闭</el-button>
        </div>
      </el-dialog>
      </main>
    </div>
  </div>
</template>

<script>
import {
  fetchThirdPartyModelList,
  createThirdPartyModel,
  updateThirdPartyModel,
  deleteThirdPartyModel,
  testThirdPartyModel,
  batchTestThirdPartyModels,
  fetchThirdPartyModelModels,
  fetchBalanceOverview,
  refreshBalanceOverview,
  fetchMobileThirdPartyModelList,
  fetchMobileThirdPartyModelDetail,
} from '../../../api/modules/thirdPartyModel'
import { isMobileDevice } from '../../../utils/device'

/**
 * 密钥脱敏展示的固定星号串。
 */
const MASKED_KEY = '••••••••••••••••'

export default {
  name: 'AiThirdPartyModel',

  data() {
    return {
      loading: false,
      /** 列表数据（全量，前端分页） */
      list: [],
      /** 当前页码 */
      page: 1,
      /** 每页条数 */
      pageSize: 10,
      /** 移动端已加载条数（触底加载的分页游标） */
      mobLoaded: 15,
      /** 移动端触底加载进行中 */
      mobLoadingFlag: false,
      /** 单个测试中的记录 ID 列表 */
      testingIds: [],
      /** 删除进行中的记录 ID 列表 */
      deletingIds: [],
      /** 批量测试进行中 */
      batchTesting: false,
      /** 已点开显示明文的记录 ID 列表 */
      visibleKeyIds: [],

      /** 新建/编辑弹窗 */
      formDialogVisible: false,
      formMode: 'create',
      submitting: false,
      /** 当前编辑的记录 ID（新建时为 null） */
      editingId: null,
      /** 当前正在编辑的行数据（用于判断是否已接入余额查询） */
      editingRow: null,
      form: {
        source: '',
        owner: '',
        url: '',
        key: '',
        loginAccount: '',
        loginPassword: '',
      },
      /** 归属是否处于「手动输入」模式（点右侧 + 按钮切换） */
      ownerCustomMode: false,
      /** 归属下拉的预设选项 */
      ownerOptions: ['自己', '烧饼', '官方'],
      /** 当前编辑记录是否已存过登录密码（用于提示「留空不修改」） */
      hasStoredPassword: false,
      formRules: {
        source: [
          { required: true, message: '请输入来源名称', trigger: 'blur' },
          { max: 128, message: '来源名称最多 128 个字符', trigger: 'blur' },
        ],
        url: [
          { required: true, message: '请输入接口地址', trigger: 'blur' },
          {
            validator: (rule, value, callback) => {
              if (!value) return callback()
              if (!/^https?:\/\//i.test(value)) {
                return callback(new Error('接口地址需以 http:// 或 https:// 开头'))
              }
              return callback()
            },
            trigger: 'blur',
          },
        ],
        key: [
          { required: true, message: '请输入接口密钥', trigger: 'blur' },
        ],
        owner: [
          { required: true, message: '请选择或输入归属', trigger: 'change' },
          { max: 64, message: '归属最多 64 个字符', trigger: 'blur' },
        ],
        loginAccount: [
          {
            validator: (rule, value, callback) => {
              if (this.formNeedsLogin && !String(value || '').trim()) {
                return callback(new Error('该来源已接入余额查询，请填写登录账号'))
              }
              return callback()
            },
            trigger: 'blur',
          },
        ],
        loginPassword: [
          {
            validator: (rule, value, callback) => {
              // 编辑时留空表示沿用旧密码，只有新建或该来源要求密码时才校验。
              if (this.formNeedsLogin && !this.hasStoredPassword && !value) {
                return callback(new Error('该来源已接入余额查询，请填写登录密码'))
              }
              return callback()
            },
            trigger: 'blur',
          },
        ],
      },

      /** 余额展示区：读缓存（进页面即渲染） */
      balanceLoading: false,
      /** 余额展示区：批量实时刷新中 */
      balanceRefreshing: false,
      /** 各来源的余额数据，结构由后端适配器决定 */
      balanceList: [],

      /** 查看详情弹窗（URL + Key + 全部模型）：移动端与桌面端共用 */
      detailDialogVisible: false,
      detailRow: null,
      detailModels: [],
      detailModelsLoading: false,
      /** 详情弹窗里 Key 是否显示明文 */
      detailKeyVisible: false,

      /** 查看模型弹窗 */
      modelsDialogVisible: false,
      modelsLoading: false,
      /** 当前查看的来源记录 */
      modelsSource: null,
      modelNames: [],
      modelsMessage: '',
      modelKeyword: '',
    }
  },

  computed: {
    /** 总条数 */
    total() {
      return this.list.length
    },

    /** 当前页展示的数据（前端分页，桌面端使用） */
    pagedList() {
      const start = (this.page - 1) * this.pageSize
      return this.list.slice(start, start + this.pageSize)
    },

    /** 移动端每次触底追加的条数 */
    mobPageSize() {
      return 15
    },

    /**
     * 移动端列表数据：从已加载条数逐步追加，滚动到底部再请求下一页。
     */
    mobList() {
      return this.list.slice(0, this.mobLoaded)
    },

    /** 移动端是否还有未加载的数据 */
    mobHasMore() {
      return this.mobLoaded < this.list.length
    },

    /** 移动端是否正在追加加载 */
    mobLoading() {
      return this.mobLoadingFlag
    },

    /** 可用数量（1 = 可用） */
    availableCount() {
      return this.list.filter(item => item.available === 1).length
    },

    /** 不可用数量（0 = 不可用，-1 未测试不计入） */
    unavailableCount() {
      return this.list.filter(item => item.available === 0).length
    },

    /**
     * 是否移动端（含 iPad / 小屏与 UA 判定），口径与全局移动端展示模式一致。
     * 移动端只展示列表，隐藏余额总览与增删改操作。
     * @returns {boolean}
     */
    isMobile() {
      return isMobileDevice()
    },

    /** 查看详情弹窗标题 */
    detailDialogTitle() {
      const source = this.detailRow ? this.detailRow.source : ''
      return source ? `查看 · ${source}` : '查看'
    },

    /** 查看模型弹窗标题 */
    modelsDialogTitle() {
      const source = this.modelsSource ? this.modelsSource.source : ''
      return source ? `${source} · 包含的模型` : '包含的模型'
    },

    /**
     * 当前表单是否需要展示登录账号密码。
     * 判定依据：编辑已有记录时看它是否已接入；新建时看填写的来源名是否已接入。
     */
    formNeedsLogin() {
      if (this.formMode === 'edit') {
        return Boolean(this.editingRow && this.editingRow.balanceSupported)
      }
      const name = String(this.form.source || '').trim()
      return this.balanceSupportedSources.indexOf(name) !== -1
    },

    /**
     * 某个来源名是否已接入余额查询方法。
     */
    balanceSupportedSources() {
      return this.list.filter(item => item.balanceSupported).map(item => item.source)
    },

    /**
     * 展示区里的余额方块。
     * 只展示「库里存在对应来源记录」的余额，避免残留已删除来源的卡片。
     */
    balanceCards() {
      const existingSources = this.list.map(item => item.source)
      return this.balanceList.filter(item => existingSources.indexOf(item.source) !== -1)
    },

    /** 按关键字过滤后的模型名 */
    filteredModelNames() {
      const keyword = this.modelKeyword.trim().toLowerCase()
      if (!keyword) return this.modelNames
      return this.modelNames.filter(name => name.toLowerCase().indexOf(keyword) !== -1)
    },
  },

  created() {
    this.loadList()
    // 移动端不展示余额总览，免登录访问时也不需要这份数据（接口本身要求登录）。
    if (!this.isMobile) this.loadBalanceOverview()
  },

  methods: {
    /**
     * 归属标签的配色类名：预设的三个值各给一种颜色，自定义值统一用默认色。
     * @param {string} owner 归属值
     * @returns {string}
     */
    ownerTagClass(owner) {
      const map = {
        自己: 'is-self',
        烧饼: 'is-shaobing',
        官方: 'is-official',
      }
      return map[owner] || 'is-custom'
    },

    /**
     * 拉取列表。
     */
    async loadList() {
      this.loading = true
      try {
        const data = this.isMobile
          ? await fetchMobileThirdPartyModelList()
          : await fetchThirdPartyModelList()
        this.list = (data && Array.isArray(data.list)) ? data.list : []
        this.clampPage()
        // 移动端刷新后回到第一屏，重新从顶部滚动加载
        this.mobLoaded = this.mobPageSize
      } catch (error) {
        // 拦截器已统一提示错误
      } finally {
        this.loading = false
      }
    },

    /**
     * 页码越界时收敛到最后一页（例如删除或筛选后总数变少）。
     */
    clampPage() {
      const maxPage = Math.max(1, Math.ceil(this.list.length / this.pageSize))
      if (this.page > maxPage) this.page = maxPage
      if (this.page < 1) this.page = 1
    },

    /**
     * 移动端列表滚动：接近底部时追加下一页数据。
     * 数据已在内存里（列表接口一次返回全量），这里只做逐步渲染，避免一次渲染上百行卡顿。
     */
    handleMobScroll() {
      const el = this.$refs.mobList
      if (!el || this.mobLoadingFlag || !this.mobHasMore) return
      // 距离底部不足一屏的 1/3 时提前加载，滚动更顺滑
      if (el.scrollTop + el.clientHeight < el.scrollHeight - 80) return
      this.mobLoadingFlag = true
      this.mobLoaded = Math.min(this.mobLoaded + this.mobPageSize, this.list.length)
      this.$nextTick(() => {
        this.mobLoadingFlag = false
      })
    },

    /**
     * 切换页码。
     * @param {number} page 目标页码
     */
    handlePageChange(page) {
      this.page = page
    },

    /**
     * 切换每页条数，并回到第一页。
     * @param {number} size 新的每页条数
     */
    handlePageSizeChange(size) {
      this.pageSize = size
      this.page = 1
      this.clampPage()
    },

    /**
     * 状态文字：可用 / 不可用 / 未测试。
     * @param {Object} row 表格行数据
     * @returns {string}
     */
    statusText(row) {
      if (row.available === 1) return '可用'
      if (row.available === 0) return '不可用'
      return '未测试'
    },

    /**
     * 状态标签样式类。
     * @param {Object} row 表格行数据
     * @returns {string}
     */
    statusClass(row) {
      if (row.available === 1) return 'is-success'
      if (row.available === 0) return 'is-danger'
      return 'is-unknown'
    },

    /**
     * 密钥脱敏展示。
     * @param {string} key 原始密钥
     * @returns {string}
     */
    maskKey(key) {
      if (!key) return '—'
      return MASKED_KEY
    },

    /**
     * 切换某行密钥的明文/密文显示。
     * @param {Object} row 表格行数据
     */
    toggleKeyVisible(row) {
      const index = this.visibleKeyIds.indexOf(row.id)
      if (index === -1) {
        this.visibleKeyIds.push(row.id)
      } else {
        this.visibleKeyIds.splice(index, 1)
      }
    },

    /**
     * 复制指定行的密钥。
     * @param {Object} row 表格行数据
     */
    copyKey(row) {
      this.copyText(row.key, '密钥已复制')
    },

    /**
     * 通用文本复制（优先使用剪贴板 API，失败时降级为 textarea 方案）。
     * @param {string} text 待复制文本
     * @param {string} successMessage 成功提示
     */
    copyText(text, successMessage) {
      const content = String(text || '')
      if (!content) {
        this.$toast.warning('没有可复制的内容')
        return
      }

      const fallbackCopy = () => {
        const textarea = document.createElement('textarea')
        textarea.value = content
        textarea.setAttribute('readonly', 'readonly')
        textarea.style.position = 'fixed'
        textarea.style.top = '-9999px'
        document.body.appendChild(textarea)
        textarea.select()
        try {
          document.execCommand('copy')
          this.$toast.success(successMessage)
        } catch (error) {
          this.$toast.error('复制失败，请手动选择复制')
        }
        document.body.removeChild(textarea)
      }

      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(content)
          .then(() => this.$toast.success(successMessage))
          .catch(fallbackCopy)
        return
      }

      fallbackCopy()
    },

    /**
     * 拉取余额展示区的数据。
     * readOnly=true 走缓存接口（快，进页面即用）；false 触发后端批量实时查询。
     * @param {boolean} [readOnly=true] 是否只读缓存。
     */
    async loadBalanceOverview(readOnly = true) {
      if (readOnly) {
        this.balanceLoading = true
      } else {
        this.balanceRefreshing = true
      }

      try {
        const data = readOnly ? await fetchBalanceOverview() : await refreshBalanceOverview()
        this.balanceList = (data && Array.isArray(data.list)) ? data.list : []

        if (!readOnly) {
          const failed = Number((data && data.failedCount) || 0)
          if (failed) {
            this.$toast.warning(`余额刷新完成，${failed} 个来源查询失败`)
          } else {
            this.$toast.success('余额已刷新')
          }
        }
      } catch (error) {
        // 拦截器已统一提示错误
      } finally {
        this.balanceLoading = false
        this.balanceRefreshing = false
      }
    },

    /**
     * 手动刷新余额（批量实时查询全部已接入来源）。
     */
    handleRefreshBalance() {
      this.loadBalanceOverview(false)
    },

    /**
     * 大数字千分位展示。
     * @param {number|string} value 原始数值
     * @returns {string}
     */
    formatNumber(value) {
      const num = Number(value)
      if (!Number.isFinite(num)) return '—'
      return num.toLocaleString('en-US')
    },

    /**
     * 时间展示。后端统一返回 Date.toString() 形态：
     *   Fri Oct 09 2026 14:16:31 GMT+0800 (China Standard Time)
     * 这里解析成 YYYY-MM-DD HH:mm:ss，解析不了就原样展示。
     * @param {string} value 后端返回的时间字符串
     * @returns {string}
     */
    formatDate(value) {
      if (!value) return '—'

      const date = new Date(value)

      if (Number.isNaN(date.getTime())) {
        return String(value)
      }

      const pad = num => String(num).padStart(2, '0')

      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} `
        + `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
    },

    /**
     * 打开新建弹窗。
     */
    openCreateDialog() {
      this.formMode = 'create'
      this.editingId = null
      this.editingRow = null
      this.hasStoredPassword = false
      // 新建时归属默认回到下拉模式，且默认选第一个预设值「自己」
      this.ownerCustomMode = false
      this.form = { source: '', owner: '自己', url: '', key: '', loginAccount: '', loginPassword: '' }
      this.formDialogVisible = true
      this.$nextTick(() => {
        if (this.$refs.formRef) this.$refs.formRef.clearValidate()
      })
    },

    /**
     * 打开编辑弹窗。
     * @param {Object} row 表格行数据
     */
    openEditDialog(row) {
      this.formMode = 'edit'
      this.editingId = row.id
      this.editingRow = row
      this.hasStoredPassword = Boolean(row.hasLoginPassword)

      // 已有归属若不在预设选项里（历史自定义值），自动进入手动输入模式，避免值被下拉框吞掉
      const ownerValue = String(row.owner || '')
      const isPresetOwner = !ownerValue || this.ownerOptions.indexOf(ownerValue) !== -1
      this.ownerCustomMode = !isPresetOwner

      this.form = {
        source: row.source || '',
        owner: ownerValue,
        url: row.url || '',
        key: row.key || '',
        loginAccount: row.loginAccount || '',
        // 密码不回显，留空表示不修改
        loginPassword: '',
      }
      this.formDialogVisible = true
      this.$nextTick(() => {
        if (this.$refs.formRef) this.$refs.formRef.clearValidate()
      })
    },

    /**
     * 切换到「自己输入归属」模式。
     * 清空当前值并聚焦输入框，让用户直接打字。
     */
    enterOwnerCustomMode() {
      this.ownerCustomMode = true
      this.form.owner = ''
      this.$nextTick(() => {
        const input = this.$el.querySelector('.owner-field__input input')
        if (input) input.focus()
      })
    },

    /**
     * 退出「自己输入归属」模式，回到下拉选择。
     * 若当前输入值恰好是预设选项则保留，否则回到第一个预设值。
     */
    exitOwnerCustomMode() {
      this.ownerCustomMode = false
      const current = String(this.form.owner || '')
      if (this.ownerOptions.indexOf(current) === -1) {
        this.form.owner = this.ownerOptions[0]
      }
    },

    /**
     * 提交新建/编辑表单。
     */
    submitForm() {
      if (!this.$refs.formRef) return

      this.$refs.formRef.validate(async (valid) => {
        if (!valid) return

        this.submitting = true
        try {
          const payload = {
            source: this.form.source,
            owner: this.form.owner,
            url: this.form.url,
            key: this.form.key,
            loginAccount: this.form.loginAccount,
            loginPassword: this.form.loginPassword,
          }

          if (this.formMode === 'create') {
            await createThirdPartyModel(payload)
            this.$toast.success('新建成功')
          } else {
            await updateThirdPartyModel(this.editingId, payload)
            this.$toast.success('保存成功')
          }

          this.formDialogVisible = false
          await this.loadList()
        } catch (error) {
          // 拦截器已统一提示错误
        } finally {
          this.submitting = false
        }
      })
    },

    /**
     * 测试单条来源是否可用。
     * @param {Object} row 表格行数据
     */
    async handleTest(row) {
      if (this.testingIds.indexOf(row.id) !== -1) return

      this.testingIds.push(row.id)
      try {
        const record = await testThirdPartyModel(row.id)
        this.$toast[record && record.available ? 'success' : 'warning'](
          `${row.source || '该来源'}：${(record && record.testMessage) || '测试完成'}`
        )
        await this.loadList()
      } catch (error) {
        // 拦截器已统一提示错误
      } finally {
        const index = this.testingIds.indexOf(row.id)
        if (index !== -1) this.testingIds.splice(index, 1)
      }
    },

    /**
     * 批量测试全部来源。
     */
    async handleBatchTest() {
      if (!this.list.length) {
        this.$toast.warning('还没有可测试的来源')
        return
      }

      try {
        await this.$confirm(
          `将依次测试全部 ${this.list.length} 个来源，耗时取决于各站点响应速度，是否继续？`,
          '批量测试',
          { type: 'warning', confirmButtonText: '开始测试', cancelButtonText: '取消' }
        )
      } catch (error) {
        return
      }

      this.batchTesting = true
      try {
        const result = await batchTestThirdPartyModels()
        const failed = result && Array.isArray(result.list)
          ? result.list.filter(item => !item.available)
          : []

        if (failed.length) {
          this.$alert(
            failed.map(item => `${item.source || `#${item.id}`}：${item.message}`).join('<br>'),
            `批量测试完成：可用 ${result.success} / ${result.total}`,
            { dangerouslyUseHTMLString: true, confirmButtonText: '知道了' }
          )
        } else {
          this.$toast.success(`批量测试完成：${result.total} 个来源全部可用`)
        }

        await this.loadList()
      } catch (error) {
        // 拦截器已统一提示错误
      } finally {
        this.batchTesting = false
      }
    },

    /**
     * 打开「查看」详情弹窗：展示 URL、Key 与全部模型。
     * 列表里已带模型清单时先用它渲染，再向接口取一次最新数据（不阻塞展示）。
     * @param {Object} row 表格行数据
     */
    async openDetailDialog(row) {
      this.detailRow = row || null
      this.detailKeyVisible = false
      this.detailModels = Array.isArray(row && row.models) ? row.models.slice() : []
      this.detailDialogVisible = true

      if (!row || row.id === undefined || row.id === null) return

      this.detailModelsLoading = true
      try {
        const data = this.isMobile
          ? await fetchMobileThirdPartyModelDetail(row.id)
          : await fetchThirdPartyModelModels(row.id)
        const models = (data && Array.isArray(data.models)) ? data.models : []
        if (models.length || !this.detailModels.length) {
          this.detailModels = models
        }
        if (data && data.url) {
          this.detailRow = { ...row, url: data.url }
        }
      } catch (error) {
        // 拦截器已统一提示错误；失败时保留列表里已有的模型清单
      } finally {
        this.detailModelsLoading = false
      }
    },

    /**
     * 复制详情弹窗里的全部模型名（换行分隔）。
     */
    copyAllDetailModels() {
      this.copyText(this.detailModels.join('\n'), `已复制 ${this.detailModels.length} 个模型名`)
    },

    /**
     * 打开「查看模型」弹窗。
     * 本地已有模型清单时直接展示，没有时后端会自动实时拉取一次。
     * @param {Object} row 表格行数据
     */
    async openModelsDialog(row) {
      this.modelsSource = row
      this.modelNames = Array.isArray(row.models) ? row.models.slice() : []
      this.modelsMessage = ''
      this.modelKeyword = ''
      this.modelsDialogVisible = true
      this.modelsLoading = true

      try {
        const data = await fetchThirdPartyModelModels(row.id)
        this.modelNames = (data && Array.isArray(data.models)) ? data.models : []
        this.modelsMessage = (data && data.message) || ''

        // 模型清单可能有变化，同步刷新列表里的数量
        await this.loadList()
      } catch (error) {
        // 拦截器已统一提示错误
      } finally {
        this.modelsLoading = false
      }
    },

    /**
     * 强制重新拉取模型清单（测试连通并刷新）。
     */
    async refreshModels() {
      if (!this.modelsSource) return

      this.modelsLoading = true
      try {
        const data = await fetchThirdPartyModelModels(this.modelsSource.id, true)
        this.modelNames = (data && Array.isArray(data.models)) ? data.models : []
        this.modelsMessage = (data && data.message) || ''

        if (!this.modelNames.length) {
          this.$toast.warning(this.modelsMessage || '该来源没有返回模型列表')
        }

        await this.loadList()
      } catch (error) {
        // 拦截器已统一提示错误
      } finally {
        this.modelsLoading = false
      }
    },

    /**
     * 复制全部模型名（换行分隔）。
     */
    copyAllModels() {
      this.copyText(this.modelNames.join('\n'), `已复制 ${this.modelNames.length} 个模型名`)
    },

    /**
     * 「更多」下拉的指令分发。
     * @param {string} command 指令名
     * @param {Object} row 表格行数据
     */
    handleMoreCommand(command, row) {
      if (command === 'edit') {
        this.openEditDialog(row)
        return
      }

      if (command === 'models') {
        this.openModelsDialog(row)
        return
      }

      if (command === 'delete') {
        this.handleDelete(row)
      }
    },

    /**
     * 删除一个来源。
     * 后端会顺带卸载该来源的余额适配器并清掉余额缓存，所以必须二次确认。
     * @param {Object} row 表格行数据
     */
    async handleDelete(row) {
      if (!row || row.id === undefined || row.id === null) return

      try {
        await this.$confirm(
          `确定删除来源「${row.source || '该来源'}」吗？删除后不可恢复。`,
          '删除确认',
          {
            confirmButtonText: '确定删除',
            cancelButtonText: '取消',
            type: 'warning',
            confirmButtonClass: 'el-button--danger',
          }
        )
      } catch (error) {
        // 用户点了取消或关闭弹窗，什么都不做
        return
      }

      this.deletingIds.push(row.id)

      try {
        const data = await deleteThirdPartyModel(row.id)
        this.$toast.success(
          (data && data.balanceMethodRemoved)
            ? `已删除「${row.source}」，并卸载了它的余额查询方法`
            : `已删除「${row.source || '该来源'}」`
        )
        await this.loadList()
        await this.loadBalanceOverview()
      } catch (error) {
        // 拦截器已统一提示错误
      } finally {
        const index = this.deletingIds.indexOf(row.id)
        if (index !== -1) this.deletingIds.splice(index, 1)
      }
    },
  },
}
</script>

<style scoped lang="scss">
.third-party-model-page {
  padding: 0;
  box-sizing: border-box;
  height: 100%;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

/* ================= 两栏主体 ================= */
/* 左侧余额总览固定宽度、固定高度、内部滚动；右侧表格自适应 */
.main-layout {
  display: flex;
  align-items: stretch;
  gap: 12px;
  /* 高度由 .third-party-model-page 撑满内容区，不再手算 calc(100vh - Npx) */
  flex: 1 1 auto;
  min-height: 0;
  box-sizing: border-box;
}

.layout-aside {
  display: flex;
  flex-direction: column;
  flex: 0 0 380px;
  width: 380px;
  height: 100%;
  min-height: 0;
  box-sizing: border-box;
}

.layout-main {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-width: 0;
  height: 100%;
  min-height: 0;
  box-sizing: border-box;
}

.meta-chip {
  display: inline-flex;
  align-items: center;
  height: 24px;
  padding: 0 10px;
  border-radius: 12px;
  font-size: 12px;
  color: #606266;
  background: var(--surface-2);
  border: 1px solid #e4e7ed;

  &.is-success {
    color: #1f9254;
    background: #eefaf3;
    border-color: #c7ecd9;
  }

  &.is-danger {
    color: #c45656;
    background: #fef0f0;
    border-color: #fbc4c4;
  }
}

/* ================= 主体卡片 ================= */
.page-card {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-height: 0;
  box-sizing: border-box;
  background: var(--surface-1);
  border: 1px solid #ebeef5;
  border-radius: 8px;
  box-shadow: var(--shadow-surface-1);
  overflow: hidden;
}

/* 表格区自适应剩余高度：数据少时也撑满，不塌陷 */
/* 分页器固定在卡片底部 */
.table-pagination {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: flex-end;
  padding: 8px 12px;
  background: var(--surface-1);
  border-top: 1px solid #ebeef5;
}

.table-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  padding: 12px 16px;
  border-bottom: 1px solid rgba(31, 45, 61, 0.06);
  background: var(--surface-1);
}

.table-toolbar__left,
.table-toolbar__right {
  display: flex;
  align-items: center;
  gap: 8px;
}

/* ================= 表格单元格 ================= */
.third-party-table {
  flex: 1 1 auto;
  width: 100%;
  min-height: 0;
}

.cell-source {
  font-weight: 600;
  color: #1f2d3d;
}

/* 归属标签：三个预设值各一种颜色，自定义值用中性色 */
.cell-owner {
  display: inline-block;
  padding: 1px 8px;
  border-radius: 10px;
  font-size: 12px;
  line-height: 18px;
  white-space: nowrap;

  &.is-self {
    color: #1f6feb;
    background: var(--surface-2);
  }

  &.is-shaobing {
    color: #b45309;
    background: #fef3c7;
  }

  &.is-official {
    color: #15803d;
    background: #dcfce7;
  }

  &.is-custom {
    color: #606266;
    background: var(--surface-2);
  }
}

/* 归属表单项：下拉/输入框 + 右侧切换按钮 */
.owner-field {
  display: flex;
  align-items: center;
  gap: 8px;

  &__select,
  &__input {
    flex: 1 1 auto;
    min-width: 0;
  }

  &__action {
    flex: 0 0 auto;
    margin-left: 0;
  }
}

.cell-url {
  color: #409eff;
  text-decoration: none;
  word-break: break-all;

  &:hover {
    text-decoration: underline;
  }
}

.cell-key {
  display: flex;
  align-items: center;
  gap: 2px;
}

.cell-key__text {
  flex: 1 1 auto;
  min-width: 0;
  font-family: Menlo, Consolas, monospace;
  font-size: 12px;
  color: #606266;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.cell-key__action {
  flex-shrink: 0;
  padding: 0 2px;
  color: #909399;

  &:hover {
    color: #409eff;
  }
}

.cell-time {
  font-size: 12px;
  color: #606266;
}

/* 状态标签 */
.status-tag {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  height: 22px;
  padding: 0 9px;
  border-radius: 11px;
  font-size: 12px;
  border: 1px solid transparent;

  .status-tag__dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: currentColor;
  }

  &.is-success {
    color: #1f9254;
    background: #eefaf3;
    border-color: #c7ecd9;
  }

  &.is-danger {
    color: #c45656;
    background: #fef0f0;
    border-color: #fbc4c4;
  }

  &.is-unknown {
    color: #909399;
    background: var(--surface-2);
    border-color: #e4e7ed;
  }
}

/* ================= 余额展示区 ================= */
.balance-board {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
  box-sizing: border-box;
  padding: 10px 14px 12px;
  background: var(--surface-1);
  border-radius: 8px;
  box-shadow: var(--shadow-surface-1);
}

.balance-board__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}

.balance-board__title {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 14px;
  font-weight: 600;
  color: #303133;
}

.balance-board__count {
  padding: 1px 8px;
  font-size: 12px;
  font-weight: 400;
  color: #909399;
  background: var(--surface-2);
  border-radius: 10px;
}

.balance-board__grid {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  gap: 10px;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
}

/* 一个来源 = 一个方块 */
.balance-card {
  flex: 0 0 auto;
  width: 100%;
  box-sizing: border-box;
  padding: 6px;
  background: var(--surface-1);
  border: 1px solid #ebeef5;
  border-radius: 8px;

  &.is-error {
    background: #fef8f8;
    border-color: #fbc4c4;
  }
}

.balance-card__head {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 6px;
}

.balance-card__source {
  font-size: 14px;
  font-weight: 600;
  color: #303133;
  text-align: center;
}

.balance-card__empty {
  padding: 10px 0;
  font-size: 12px;
  color: #c45656;
}

.bl-fallback {
  font-size: 13px;
  color: #303133;
}

/* 商汤：单个积分池 */
/* 积分池：多个池子从左往右排 */
/* 积分池：从上往下排列，每个撑满整个卡片宽度 */
.balance-card__pools {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.bl-pool {
  width: 100%;
  box-sizing: border-box;
  padding: 10px 12px;
  background: var(--surface-1);
  border: 1px solid #ebeef5;
  border-radius: 6px;
}

.bl-pool__head {
  margin-bottom: 8px;
}

/* 可用范围单独占一行 */
.bl-pool__scope {
  display: block;
  margin-top: 2px;
}

.bl-pool__name {
  font-size: 13px;
  font-weight: 600;
  color: #303133;
}

.bl-pool__scope {
  font-size: 11px;
  color: #909399;
}

.bl-pool__reset {
  margin-top: 8px;
  font-size: 11px;
  color: #a0a4ab;
}

/* 池内三个元素从上往下排列 */
.bl-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.bl-grid--vertical {
  flex-direction: column;
  flex-wrap: nowrap;
}

.bl-cell {
  flex: 0 0 auto;
  padding: 8px 10px;
  background: var(--surface-1);
  border-radius: 6px;

  &.is-highlight {
    background: #f2fbf6;
  }
}

.bl-cell__label {
  margin-bottom: 4px;
  font-size: 11px;
  color: #909399;
}

.bl-cell__value {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
  line-height: 1.3;

  &.is-primary {
    color: #1f9254;
  }
}

.bl-cell__sub {
  margin-top: 2px;
  font-size: 11px;
  color: #909399;
}

.bl-bar {
  height: 4px;
  margin: 6px 0 4px;
  overflow: hidden;
  background: #e9ecf0;
  border-radius: 2px;

  i {
    display: block;
    height: 100%;
    background: #409eff;
    border-radius: 2px;
    transition: width 0.3s;
  }
}

.cell-model-count {
  padding: 0;
  font-weight: 600;
}

/* 操作栏「更多」下拉触发区 */
.row-more {
  margin-left: 8px;
  font-size: 12px;
  color: #409eff;
  cursor: pointer;
  user-select: none;

  &:hover {
    color: #66b1ff;
  }
}

/* 空状态 */
.table-empty {
  padding: 28px 0;
  text-align: center;
  color: #909399;
  font-size: 13px;

  p {
    margin: 0 0 12px;
  }
}

/* ================= 表单弹窗 ================= */
.form-tip {
  margin-top: 4px;
  font-size: 12px;
  line-height: 1.6;
  color: #909399;

  code {
    padding: 1px 5px;
    border-radius: 3px;
    background: var(--surface-2);
    color: #606266;
    font-family: Menlo, Consolas, monospace;
  }
}

/* ================= 查看模型弹窗 ================= */
.models-body {
  min-height: 160px;
}

.models-message {
  margin-bottom: 10px;
  padding: 8px 12px;
  border-radius: 6px;
  background: var(--surface-2);
  font-size: 12px;
  color: #606266;
}

.models-toolbar {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
}

.models-search {
  flex: 1 1 auto;
  max-width: 240px;
}

.models-list {
  margin: 0;
  padding: 0;
  list-style: none;
  max-height: 360px;
  overflow-y: auto;
  border: 1px solid rgba(31, 45, 61, 0.06);
  border-radius: 6px;
}

.models-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 7px 12px;
  font-size: 13px;
  color: #303133;
  border-bottom: 1px solid rgba(31, 45, 61, 0.06);

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: var(--surface-1);
  }
}

.models-item__name {
  flex: 1 1 auto;
  min-width: 0;
  font-family: Menlo, Consolas, monospace;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.models-item__copy {
  flex-shrink: 0;
  padding: 0 2px;
  color: #909399;

  &:hover {
    color: #409eff;
  }
}

.models-empty {
  padding: 28px 0;
  text-align: center;
  color: #909399;
  font-size: 13px;
}

/* ================= 移动端列表（只保留 来源 / 是否可用 / 查看） ================= */
.mob-list {
  /* 固定高度容器，超出后内部滚动；高度随视口计算，兼容 iPhone 地址栏伸缩 */
  flex: 0 0 auto;
  height: min(64vh, 560px);
  overflow-y: auto;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
  background: var(--surface-1);
}

.mob-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 14px;
  border-bottom: 1px solid rgba(31, 45, 61, 0.06);

  &:last-child {
    border-bottom: none;
  }

  &:active {
    background: var(--surface-1);
  }
}

.mob-row__source {
  flex: 1 1 auto;
  min-width: 0;
  font-size: 15px;
  font-weight: 600;
  color: #1f2d3d;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.mob-row__status {
  flex: 0 0 auto;
}

.mob-row__actions {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 2px;
}

.mob-row__action {
  flex: 0 0 auto;
  padding: 0 4px;
  /* 便于 iPhone 上手指点按 */
  min-height: 32px;
  font-size: 14px;
}

/* 触底加载状态 */
.mob-list__footer {
  padding: 14px 0 18px;
  text-align: center;
  font-size: 12px;
  color: #a8adb5;
}

/* ================= 查看详情弹窗 ================= */
.detail-body {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.detail-item__label {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 6px;
  font-size: 12px;
  color: #909399;
}

.detail-item__count {
  color: #606266;
}

.detail-item__copy {
  padding: 0;
  font-size: 12px;
}

.detail-item__value {
  display: block;
  padding: 9px 12px;
  font-size: 13px;
  line-height: 1.5;
  color: #1f2d3d;
  background: var(--surface-1);
  border: 1px solid rgba(31, 45, 61, 0.06);
  border-radius: 8px;
  word-break: break-all;

  &.detail-item__link {
    color: #409eff;
    text-decoration: none;
  }

  &.is-key {
    display: flex;
    align-items: center;
    gap: 8px;
    font-family: Menlo, Consolas, monospace;
    cursor: pointer;
    user-select: none;
  }
}

.detail-item__tip {
  margin-top: 4px;
  font-size: 11px;
  color: #a0a4ab;
}

.detail-models {
  max-height: 40vh;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  border: 1px solid rgba(31, 45, 61, 0.06);
  border-radius: 8px;
}

.detail-model {
  padding: 10px 12px;
  font-family: Menlo, Consolas, monospace;
  font-size: 13px;
  color: #303133;
  border-bottom: 1px solid rgba(31, 45, 61, 0.06);
  word-break: break-all;

  &:last-child {
    border-bottom: none;
  }

  &:active {
    background: var(--surface-2);
  }
}

.detail-models__empty {
  padding: 24px 0;
  text-align: center;
  font-size: 13px;
  color: #909399;
}

/* 移动端（iPhone Safari）细节：隐藏余额总览后列表撑满、分页器换行 */
.third-party-model-page.is-mobile {
  .layout-main {
    width: 100%;
  }

  .page-card {
    border-radius: 0;
    border-left: none;
    border-right: none;
    box-shadow: none;
  }

  /* 移动端工具条只留下数量统计 */
  .table-toolbar {
    padding: 10px 12px;
  }

  .table-toolbar__right {
    flex-wrap: wrap;
  }

  .table-pagination {
    flex-wrap: wrap;
    gap: 6px;
    justify-content: center;
  }
}

/* ================= 小屏适配 ================= */
@media (max-width: 768px) {
  .third-party-model-page {
    padding: 0;
  }

  /* 小屏改回上下堆叠，各占一半高度 */
  .main-layout {
    flex-direction: column;
    height: auto;
  }

  .layout-aside {
    flex: 0 0 auto;
    width: 100%;
    height: auto;
    max-height: 320px;
  }

  .layout-main {
    width: 100%;
    height: auto;
  }

  .table-toolbar {
    padding: 10px 12px;
  }

  .mob-row {
    padding: 13px 12px;
  }
}
</style>

<style lang="scss">
/* 弹窗内部结构由子组件渲染，需非 scoped 样式命中 */
.third-party-models-dialog .el-dialog__body {
  padding-top: 16px;
}

.third-party-detail-dialog .el-dialog__body {
  padding: 16px 20px 4px;
}

.third-party-form-dialog .el-form-item {
  margin-bottom: 18px;
}
</style>
